import React, { useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, CircularProgress, Button, TableSortLabel, Card, CardContent, useMediaQuery, useTheme, Chip } from '@material-ui/core';

import { LinkOff, FilterList } from '@material-ui/icons';
// import { StyledTablePagination } from '../../../customUI/table/TableStyles'; // Commented out - pagination UI hidden
import useStyles from '../styles/PremiumBacklink.style'; 
import usePremiumBacklink from '../hooks/usePremiumBacklink';
import AppliedFilters from './AppliedFilters';
import FilterInput from './FilterInput';

const PremiumBacklinksTable = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const {
    availableBacklinks,
    isFetchingBacklinks,
    // pagination, // Commented out - pagination UI hidden
    // handleChangePage, // Commented out - pagination UI hidden
    // handleChangeRowsPerPage, // Commented out - pagination UI hidden
    filters,
    handleFilterChange,
    columns,
    sorting,
    handleSortChange,
    createPurchaseCell,
  } = usePremiumBacklink();

  const hasBacklinks = availableBacklinks && availableBacklinks.length > 0;
  const [showFilters, setShowFilters] = useState(true);

  // Column configuration


  // Local filter state for inputs (to avoid API calls on every keystroke)
  const [localFilters, setLocalFilters] = useState({
    name: filters.name,
    domain: filters.domain,
    dr: filters.dr,
    ok: filters.ok,
    ot: filters.ot,
    price: filters.price,
  });

  // Update local filters when Redux filters change (e.g., from applied filters chips)
  React.useEffect(() => {
    setLocalFilters({
      name: filters.name,
      domain: filters.domain,
      dr: filters.dr,
      ok: filters.ok,
      ot: filters.ot,
      price: filters.price,
    });
  }, [filters]);

  // Handle local filter changes (typing)
  const handleLocalFilterChange = (field, value) => {
    setLocalFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply filter (on Enter or blur)
  const applyFilter = (field, value) => {
    handleFilterChange({ [field]: value });
  };

  // Handle Enter key press
  const handleKeyPress = (event, field) => {
    if (event.key === 'Enter') {
      applyFilter(field, event.target.value);
    }
  };

  // Handle blur (focus lost)
  const handleBlur = (field) => {
    applyFilter(field, localFilters[field]);
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(prev => !prev);
  };

  // Render filter cell
  const renderFilterCell = (column) => {
    const { id, align, filter, width } = column;
    
    if (!filter) {
      return <TableCell key={`filter-${id}`} align={align} width={width} style={{ padding: '2px 4px', borderBottom: '1px solid #e0e0e0', height: '32px' }} />;
    }

    const { type, placeholder, options = [], fullWidth = false, style = {} } = filter;

    if (type === 'select') {
      return (
        <TableCell key={`filter-${id}`} align={align} width={width} style={{ padding: '2px 4px', borderBottom: '1px solid #e0e0e0', height: '32px' }}>
          <FilterInput
            type={type}
            placeholder={placeholder}
            options={options}
            fullWidth={fullWidth}
            style={style}
            value={filters[id]}
            onChange={(value) => handleFilterChange({ [id]: value })}
          />
        </TableCell>
      );
    }

    return (
      <TableCell key={`filter-${id}`} align={align} width={width} style={{ padding: '2px 4px', borderBottom: '1px solid #e0e0e0', height: '32px' }}>
        <FilterInput
          type={type}
          placeholder={placeholder}
          options={options}
          fullWidth={fullWidth}
          style={style}
          value={localFilters[id]}
          onChange={(value) => handleLocalFilterChange(id, value)}
          onKeyPress={(event) => handleKeyPress(event, id)}
          onBlur={() => handleBlur(id)}
        />
      </TableCell>
    );
  };

  // Render mobile card for each backlink
  const renderMobileCard = (row) => (
      <Card key={row.id} style={{ marginBottom: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <CardContent style={{ padding: '16px' }}>
          {/* Header with name and price */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 600, color: '#333', flex: 1, marginRight: '8px' }}>
              {row.name}
            </Typography>
            <Typography variant="h6" style={{ fontSize: '18px', fontWeight: 700, color: '#1a3552' }}>
              ${row.price?.toLocaleString() || '0'}
            </Typography>
          </Box>

          {/* Domain and DR */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="body2" style={{ color: '#666', fontSize: '12px', marginBottom: '2px' }}>
                Domain
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500, color: '#333' }}>
                {row.domain}
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="body2" style={{ color: '#666', fontSize: '12px', marginBottom: '2px' }}>
                DR
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#1a3552' }}>
                {row.dr || '-'}
              </Typography>
            </Box>
          </Box>

          {/* Turnaround and Probability */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="body2" style={{ color: '#666', fontSize: '12px', marginBottom: '2px' }}>
                Turnaround
              </Typography>
              <Chip
                label={row.turn_around || '-'}
                size="small"
                style={{
                  backgroundColor: row.turn_around === 'Short' ? '#fb842e42' : '#9c27b02e',
                  color: row.turn_around === 'Short' ? '#ff6b00' : '#9c27b0',
                  fontSize: '11px',
                  height: '20px'
                }}
              />
            </Box>
            <Box textAlign="right">
              <Typography variant="body2" style={{ color: '#666', fontSize: '12px', marginBottom: '2px' }}>
                Probability
              </Typography>
              <Chip
                label={row.probability || '-'}
                size="small"
                style={{
                  backgroundColor: (() => {
                    if (row.probability === 'High') return '#ffebee';
                    if (row.probability === 'Medium') return '#fff3e0';
                    return '#e8f5e9';
                  })(),
                  color: (() => {
                    if (row.probability === 'High') return '#ff1744';
                    if (row.probability === 'Medium') return '#ff6b00';
                    return '#00c853';
                  })(),
                  fontSize: '11px',
                  height: '20px'
                }}
              />
            </Box>
          </Box>

          {/* OK and OT */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="body2" style={{ color: '#666', fontSize: '12px', marginBottom: '2px' }}>
                OK
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500, color: '#333' }}>
                {row.ok || '-'}
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="body2" style={{ color: '#666', fontSize: '12px', marginBottom: '2px' }}>
                OT
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500, color: '#333' }}>
                {row.ot || '-'}
              </Typography>
            </Box>
          </Box>

          {/* Purchase Controls */}
          <Box display="flex" justifyContent="center" mt={2}>
            {createPurchaseCell && typeof createPurchaseCell === 'function' ? createPurchaseCell(row) : null}
          </Box>
        </CardContent>
      </Card>
  );

  // Render table body content based on state
  const renderTableBody = () => {
    if (isFetchingBacklinks) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} style={{ textAlign: 'center', padding: '40px 16px', height: '200px' }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              style={{ color: '#666', height: '100%' }}
            >
              <CircularProgress size={48} style={{ marginBottom: 16, color: '#12344E' }} />
              <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
                Loading Premium Backlinks...
              </Typography>
              <Typography variant="body2" style={{ color: '#999', maxWidth: 400 }}>
                Please wait while we load the available premium backlinks for your website.
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    if (!hasBacklinks) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} style={{ textAlign: 'center', padding: '40px 32px', height: '200px', minWidth: '500px' }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              style={{ color: '#666', height: '100%', maxWidth: '600px', margin: '0 auto' }}
            >
              <LinkOff
                style={{
                  fontSize: 64,
                  color: '#ccc',
                  marginBottom: 16
                }}
              />
              <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
                No Premium Backlinks Available
              </Typography>
              <Typography variant="body2" style={{ 
                color: '#999', 
                maxWidth: '100%', 
                textAlign: 'center',
                wordWrap: 'break-word',
                lineHeight: '1.4'
              }}>
                Premium backlinks are currently being loaded or none are available at the moment.
                Please check back later or contact support if this persists.
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    return availableBacklinks.map((row) => (
      <TableRow key={row.id}>
        {columns.map((column) => (
          <TableCell 
            key={column.id} 
            align={column.align}
            width={column.width}
            component={column.id === 'name' ? 'th' : 'td'}
            scope={column.id === 'name' ? 'row' : undefined}
            className={column.id === 'turn_around' ? classes.turnaround : undefined}
          >
            {column.render(row)}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  // Render mobile cards view
  if (isMobile) {
    return (
      <Box pb={2}>
        <AppliedFilters />
        <div className={classes.card}>
          {(() => {
            if (isFetchingBacklinks) {
              return (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  style={{ color: '#666', height: '200px', padding: '40px 16px' }}
                >
                  <CircularProgress size={48} style={{ marginBottom: 16, color: '#12344E' }} />
                  <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
                    Loading Premium Backlinks...
                  </Typography>
                  <Typography variant="body2" style={{ 
                    color: '#999', 
                    maxWidth: '100%', 
                    textAlign: 'center',
                    padding: '0 16px',
                    wordWrap: 'break-word',
                    lineHeight: '1.4'
                  }}>
                    Please wait while we load the available premium backlinks for your website.
                  </Typography>
                </Box>
              );
            }
            if (!hasBacklinks) {
              return (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  style={{ color: '#666', height: '200px', padding: '40px 16px' }}
                >
                  <LinkOff
                    style={{
                      fontSize: 64,
                      color: '#ccc',
                      marginBottom: 16
                    }}
                  />
                  <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
                    No Premium Backlinks Available
                  </Typography>
                  <Typography variant="body2" style={{ 
                    color: '#999', 
                    maxWidth: '100%', 
                    textAlign: 'center',
                    padding: '0 16px',
                    wordWrap: 'break-word',
                    lineHeight: '1.4'
                  }}>
                    Premium backlinks are currently being loaded or none are available at the moment.
                    Please check back later or contact support if this persists.
                  </Typography>
                </Box>
              );
            }
            return (
              <Box>
                {availableBacklinks.map((row) => renderMobileCard(row))}
              </Box>
            );
          })()}
        </div>
      </Box>
    );
  }

  // Desktop table view
  return (
    <Box pb={2}>
      <AppliedFilters />
      <div className={classes.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Button onClick={toggleFilters} style={{ display: 'none', alignItems: 'center', gap: '4px' }}>
            {showFilters ? <FilterList style={{ color: '#12344E' }} /> : <FilterList style={{ color: '#12344E', opacity: 0.5 }} />}
            <Typography variant="body2" style={{ color: '#12344E', fontWeight: 500 }}>
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Typography>
          </Button>
        </div>
        <div className={classes.tableWrapper}>
          <TableContainer className={classes.tableContainer} style={{ height: '300px', position: 'relative' }}>
            <Table className={classes.table} aria-label="premium backlinks table">
              <TableHead>
                {/* Header Row */}
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align} width={column.width}>
                      {column.id === 'purchase' ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={sorting.orderBy === column.id}
                          direction={sorting.orderBy === column.id ? sorting.order : 'asc'}
                          onClick={() => handleSortChange(column.id)}
                          IconComponent="span"
                          style={{ flexDirection: 'row' }}
                        >
                          {column.label}
                          <span style={{ marginLeft: '4px' }}>
                            {(() => {
                              if (sorting.orderBy === column.id) {
                                return sorting.order === 'asc' ? '↑' : '↓';
                              }
                              return '↕';
                            })()}
                          </span>
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                {/* Filter Row */}
                {showFilters && (
                  <TableRow>
                    {columns.map((column) => renderFilterCell(column))}
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {renderTableBody()}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        {/* Pagination UI commented out - fetching all records */}
        {/* <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 16px', borderTop: '1px solid #e0e0e0' }}>
          <StyledTablePagination
            rowsPerPageOptions={[10, 15, 25, 50, 100]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.perPage}
            page={pagination.currentPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows:"
            size="small"
          />
        </div> */}
      </div>
    </Box>
  );
};

export default PremiumBacklinksTable; 